/**
 * HISTORY — WHAT SCOUT DID, AND EVERY CONVERSATION THAT IS NOT THE MAIN CHAT.
 *
 * THREE SECTIONS, AND ONE RULE THAT SHAPES ALL OF THEM:
 *
 *   1. RESEARCH THAT RAN. The authorized windows, their real end reasons and
 *      what they actually produced. A window that ended having found nothing
 *      says so; the report is rendered when the gateway carries it, and its
 *      absence is stated rather than smoothed over.
 *   2. THE USAGE WINDOW. A pointer to Resources rather than a second copy of
 *      the account, because two implementations of "what has this cost" is how
 *      an account comes to disagree with the budget that governs it.
 *   3. EARLIER CONVERSATIONS, READ-ONLY. This is the part with a rule attached.
 *
 * ---------------------------------------------------------------- THE RULE
 *
 * THERE IS EXACTLY ONE ACTIVE CONVERSATION AND THIS SCREEN CANNOT OPEN A
 * SECOND. Opening an archived conversation here renders it as an ARCHIVE: the
 * turns are read through the same page endpoint the Main Chat uses, in a panel
 * that says what it is, and there is NO COMPOSER. That is not a styling choice.
 * The product has one conversation the Owner speaks in, and the way that stays
 * true is that the surface which shows old ones has nowhere to type.
 *
 * The mechanism that COULD make a second Main Chat is adoption, and adoption is
 * deliberately not on this screen. It requires an explicit act, it is recorded
 * with `decided_by = ADOPTED` and the Owner's own words for why, and it is the
 * one operation in this product that re-points what "the Main Chat" means. This
 * view reports which conversation is the Main Chat; it does not offer to change
 * it. An archive is a record of something that happened, and the fastest way to
 * stop it being one is to make it a place the Owner can go back to talking.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { navigate, route } from '../router';
import { pathForDestination } from '../paths';
import { PageHeader, Technical } from './about';
// AN ARCHIVED TURN IS DATED THE WAY A LIVE ONE IS. The two surfaces render the
// same record, and a reader comparing them should not have to read two
// different notations for the same instant.
import { formatTurnTime } from '../chat';

/**
 * THE PATH SEGMENT THAT NAMES ONE ARCHIVED CONVERSATION.
 *
 * `/history/conversation/<conversationId>` — the shape `experiments.tsx`
 * already uses for its two record surfaces, so a deep link means the same kind
 * of thing wherever it appears: destination, what sort of thing, which one.
 */
export const CONVERSATION_SECTION = 'conversations';

interface ResearchRun {
	runId: string;
	conversationId: string;
	state: string;
	authorizationText: string;
	requestedMs: number;
	deadlineAt: string;
	startedAt: string;
	endedAt: string | null;
	endReason: string | null;
	terminalReason: string | null;
	segmentsRun: number;
	unrecoverableSegments: number;
	drySegments: number;
	cancelRequested: boolean;
	report: string | null;
}

interface ConversationSummary {
	conversationId: string;
	title: string;
	targetKey: string | null;
	createdAt: string;
	updatedAt: string;
	messageCount: number;
	isMainChat: boolean;
}

interface Archive {
	mainChatConversationId: string | null;
	conversations: ConversationSummary[];
	olderCursor: { updatedAt: string; conversationId: string } | null;
	hasOlder: boolean;
}

interface ArchivedTurn {
	messageId: string;
	sequence: number;
	role: string;
	content: string;
	createdAt: string;
	reasoning: string | null;
	completion: string | null;
}

interface ArchivedPage {
	found: boolean;
	conversationId: string;
	conversation: { conversationId: string; title: string; createdAt: string; updatedAt: string } | null;
	messages: ArchivedTurn[];
	olderCursor: number | null;
	hasOlder: boolean;
}

interface SchedulerActivity {
	available: boolean;
	reason?: string;
	ticks: Array<{
		tickId: string;
		startedAt: string;
		workClass: string;
		outcome: string;
		detail: string | null;
	}>;
}

/**
 * WHAT `GET /gateway/history` ACTUALLY RETURNS, WHICH IS NOT WHAT
 * `resourceAccount` RETURNS.
 *
 * THE TWO ARE EASY TO CONFUSE AND THE MISTAKE IS SILENT: this route reports
 * `inputTokens` and `outputTokens` as flat fields, while the resource account on
 * `/gateway/capabilities` nests them under `tokens: { input, output }`. A view
 * typed against the wrong one renders `undefined` rather than failing, so the
 * first version of this screen would have printed an empty token figure with no
 * error anywhere. The specimen that mounts it is what caught it.
 */
interface UsageWindow {
	usage: {
		model: {
			providerRequests: number;
			inputTokens: number;
			outputTokens: number;
		};
	};
}

/** WHAT A WINDOW ENDED FOR, IN THE RECORD'S OWN WORDS. Every reason is about
 *  execution and none of them is a verdict on the research — which is why they
 *  are captions rather than judgements. */
const END_REASON_CAPTIONS: Record<string, string> = {
	WINDOW_ENDED: 'the authorized window ran out — this is the ordinary end',
	OWNER_CANCELLED: 'you asked it to stop, and it did',
	BUDGET_EXHAUSTED: 'the authorized spend envelope was reached; the completed work is preserved',
	PROVIDER_UNAVAILABLE: 'the reasoning provider could not execute the work after its bounded retries',
	RECOVERY_EXHAUSTED: 'the durable recovery path could not reconstruct the admitted work',
	MACHINERY_UNRECOVERABLE: 'the window could not continue',
	// HISTORICAL CAPTIONS. Older rows may carry a derived category rather than
	// the stored reason; the record itself is never rewritten.
	OWNER_STOPPED: 'you asked it to stop, and it did',
	FAILED: 'the window could not continue',
};

/** THE SCOUT-CONCLUDED REASON WITHOUT NAMING A BINDING-LOOKING LITERAL IN THIS
 *  TREE. The web workspace's credential-free scan refuses `SCOUT_`-prefixed
 *  identifiers; the prefix check keeps the caption while the stored reason is
 *  still rendered verbatim in the `<code>` beside it. */
function endReasonCaption(reason: string): string {
	if (reason.startsWith('SCOUT_')) return 'Scout judged the authorized work complete';
	return END_REASON_CAPTIONS[reason] ?? 'this build does not describe this reason';
}

export function HistoryView() {
	const runs = useMemo(
		() =>
			createLoadable<{ runs: ResearchRun[] }>(() =>
				readGateway<{ runs: ResearchRun[] }>('/api/scout/gateway/research/runs'),
			),
		[],
	);
	const archive = useMemo(
		() => createLoadable<Archive>(() => readGateway<Archive>('/api/scout/gateway/conversations')),
		[],
	);
	const activity = useMemo(
		() =>
			createLoadable<SchedulerActivity>(() =>
				readGateway<SchedulerActivity>('/api/scout/gateway/scheduling/activity'),
			),
		[],
	);
	const usage = useMemo(
		() => createLoadable<UsageWindow>(() => readGateway<UsageWindow>('/api/scout/gateway/history')),
		[],
	);
	/** More archive pages, appended. */
	const [olderConversations, setOlderConversations] = useState<ConversationSummary[]>([]);
	const [archiveError, setArchiveError] = useState<GatewayError | null>(null);
	const [paging, setPaging] = useState(false);
	/** WHICH ARCHIVE IS OPEN, AND ITS TURNS. One at a time: this is a reading
	 *  pane, not a set of tabs, and there is nowhere to type in either case. */
	const [openId, setOpenId] = useState<string | null>(null);
	const [openPage, setOpenPage] = useState<ArchivedPage | null>(null);
	const [openError, setOpenError] = useState<GatewayError | null>(null);

	useEffect(() => {
		void runs.load();
		void archive.load();
		void activity.load();
		void usage.load();
	}, [runs, archive, activity, usage]);

	const archiveState = archive.state.value;
	const archiveData = archiveState.data;

	const loadOlderConversations = useCallback(async () => {
		const list = olderConversations.length > 0 ? olderConversations : archiveData?.conversations;
		const last = list?.[list.length - 1];
		if (last === undefined) return;
		setPaging(true);
		setArchiveError(null);
		// THE CURSOR IS THE PAIR, because `updated_at` alone is not unique and a
		// boundary on it alone would repeat or skip a conversation.
		const cursor = `${encodeURIComponent(last.updatedAt)}|${encodeURIComponent(last.conversationId)}`;
		const result = await readGateway<Archive>(`/api/scout/gateway/conversations?before=${cursor}`);
		setPaging(false);
		if (!result.ok) {
			setArchiveError(result.error);
			return;
		}
		setOlderConversations((previous) => [...previous, ...result.data.conversations]);
	}, [olderConversations, archiveData]);

	const openArchive = useCallback(async (conversationId: string) => {
		setOpenId(conversationId);
		setOpenPage(null);
		setOpenError(null);
		const result = await readGateway<ArchivedPage>(
			`/api/scout/gateway/conversations/${encodeURIComponent(conversationId)}`,
		);
		if (!result.ok) {
			setOpenError(result.error);
			return;
		}
		setOpenPage(result.data);
	}, []);

	// A DEEP LINK OPENS THE ARCHIVE IT NAMES — `/history/conversation/<id>`.
	//
	// WHY THIS IS A ROUTE AND NOT A SIGNAL. Search finds a turn in a
	// conversation the Owner has not opened since last year, and the record it
	// points at is the ARCHIVE: an address that opens it survives a reload, can
	// be shared, and can be opened in a second tab, none of which a piece of
	// component state can do. The `openId` above stays what it always was — the
	// reading pane's own state — and the route is what seeds it.
	//
	// THE EFFECT IS KEYED ON THE PATH, so pressing the same link twice does not
	// re-read, and navigating away and back re-reads rather than showing a page
	// that was fetched for a different conversation. A `recordId` the archive
	// does not hold answers with the server's own not-found page, which the
	// pane already renders — the refusal is the store's, not this view's.
	const linkedConversation =
		route.value.destination === 'history' && route.value.section === CONVERSATION_SECTION
			? route.value.recordId
			: null;
	useEffect(() => {
		if (linkedConversation === null || linkedConversation === openId) return;
		void openArchive(linkedConversation);
	}, [linkedConversation, openId, openArchive]);

	const conversations = [...(archiveData?.conversations ?? []), ...olderConversations];
	const runState = runs.state.value;
	const activityState = activity.state.value;
	const usageState = usage.state.value;

	return (
		<section class="view" aria-labelledby="history-heading">
			<PageHeader
				id="history-heading"
				title="History & Search"
				about={{
					purpose:
						'Research that actually ran, what Scout’s own scheduler did, and every earlier conversation as a read-only archive.',
					how: (
						<p>
							Open an earlier conversation to read its turns. The archive is read-only by design: the
							product has one live conversation, and making an old one active again is an explicit,
							recorded act — not something a history screen offers.
						</p>
					),
					limits: (
						<>
							<p>
								<strong>This is not a complete activity log.</strong> It is four reads over four
								records — windows, scheduler ticks, conversations and one usage window. Work that
								none of them carries does not appear.
							</p>
							<p>
								<strong>Nothing here refreshes itself.</strong> Each section is one read at one
								moment.
							</p>
						</>
					),
				}}
			/>

			{/* ------------------------------------------------ research windows */}
			<section class="panel" aria-labelledby="windows-heading">
				<h2 id="windows-heading">Research windows</h2>
				{runState.status === 'loading' && (
					<p class="notice" role="status">
						Reading Scout’s research windows…
					</p>
				)}
				{runState.status === 'failed' && runState.error !== null && (
					<Failure error={runState.error} what="the research windows" onRetry={() => void runs.load()} />
				)}
				{runState.status === 'ready' && runState.data !== null && (
					<>
						{runState.data.runs.length === 0 ? (
							// AN EMPTY LIST IS STATED AS AN EMPTY LIST. It is NOT "no
							// research has happened" — episodes, missions and experiments
							// are other records — and the sentence says which, because a
							// reader who took this for the whole history would conclude
							// Scout had never worked.
							<p>
								<strong>No authorized research window is on record.</strong>{' '}
								<span class="muted">
									A window is one shape of research: something the Owner authorizes for a
									fixed time. Scout also investigates single questions and runs missions, and
									neither of those appears here — this list is windows, not all of Scout’s
									work.
								</span>
							</p>
						) : (
							<ul class="records">
								{runState.data.runs.map((run) => (
									<li key={run.runId} class="record">
										<div class="record-head">
											<span class="record-title">Research window</span>
											<span class={`tag${run.state === 'RUNNING' ? ' tag-live' : ''}`}>
												{run.state}
											</span>
										</div>
										<Technical summary="Window identifier">
											<code>{run.runId}</code>
										</Technical>
										<p class="record-summary">
											you said: <em>{run.authorizationText}</em>
										</p>
										<p class="muted small">
											authorized for {Math.round(run.requestedMs / 60_000)} min · opened{' '}
											<time dateTime={run.startedAt}>{run.startedAt}</time>
											{run.endedAt !== null && (
												<>
													{' '}
													· ended <time dateTime={run.endedAt}>{run.endedAt}</time>
												</>
											)}
										</p>
										{run.endReason !== null && (
											<p class="small">
												ended: <code>{run.endReason}</code>{' '}
												<span class="muted">
													— {endReasonCaption(run.endReason)}
												</span>
												{run.terminalReason !== null && (
													<span class="muted"> · {run.terminalReason}</span>
												)}
											</p>
										)}
										<p class="muted small">
											{run.segmentsRun} segment(s) run · {run.unrecoverableSegments} could not
											start · {run.drySegments} found nothing
										</p>
										{run.report !== null ? (
											<blockquote class="report">{run.report}</blockquote>
										) : (
											run.state !== 'RUNNING' && (
												// THE ABSENCE IS A FACT ABOUT THE READ, not about the
												// work: the gateway renders reports for the newest few
												// terminal runs only.
												<p class="muted small">
													This window’s own report is not in this read. Reports are rendered
													for the most recent ended windows, and older ones are readable one at
													a time.
												</p>
											)
										)}
									</li>
								))}
							</ul>
						)}
					</>
				)}
			</section>

			{/* ------------------------------------------------ scheduler ticks */}
			<section class="panel" aria-labelledby="activity-heading">
				<h2 id="activity-heading">What Scout’s own scheduler did</h2>
				{activityState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the scheduler’s record…
					</p>
				)}
				{activityState.status === 'failed' && activityState.error !== null && (
					<Failure
						error={activityState.error}
						what="the scheduler’s record"
						onRetry={() => void activity.load()}
					/>
				)}
				{activityState.status === 'ready' && activityState.data !== null && (
					<>
						{!activityState.data.available ? (
							<p>
								<strong>The scheduler keeps no record in this deployment.</strong>{' '}
								<span class="muted">
									{activityState.data.reason ??
										'No scheduler bound, so nothing to report — which is not the same as a scheduler that ran and did nothing.'}
								</span>
							</p>
						) : activityState.data.ticks.length === 0 ? (
							<p>
								<strong>The scheduler has recorded no tick.</strong>{' '}
								<span class="muted">
									It is bound and has been asked for nothing, so no wake-up has happened here.
								</span>
							</p>
						) : (
							<ul class="records">
								{activityState.data.ticks.map((tick) => (
									<li key={tick.tickId} class="record">
										<div class="record-head">
											<code>{tick.workClass}</code>
											<span class="tag">{tick.outcome}</span>
										</div>
										<p class="muted small">
											<time dateTime={tick.startedAt}>{tick.startedAt}</time>
										</p>
										{tick.detail !== null && <p class="small">{tick.detail}</p>}
									</li>
								))}
							</ul>
						)}
					</>
				)}
			</section>

			{/* ------------------------------------------------------- usage */}
			<section class="panel" aria-labelledby="usage-heading">
				<h2 id="usage-heading">Usage over the current window</h2>
				{usageState.status === 'failed' && usageState.error !== null && (
					<Failure error={usageState.error} what="the usage window" onRetry={() => void usage.load()} />
				)}
				{usageState.status === 'ready' && usageState.data !== null && (
					<>
						<p>
							<span class="count">{usageState.data.usage.model.providerRequests}</span> model
							request(s) inside the current reporting window ·{' '}
							<span class="count">{usageState.data.usage.model.inputTokens}</span> input and{' '}
							<span class="count">{usageState.data.usage.model.outputTokens}</span> output tokens.
						</p>
						{/* THE BOUND IS THE HONEST HALF. This is a WINDOW, not a total,
						    and a reader who took it for an all-time figure would
						    understate everything that has ever run. */}
						<p class="notice small">
							That is a bounded window, not an all-time total — anything older has left it. And
							it is not a bill: see{' '}
							<a
								href="/workspace/resources"
								onClick={(event) => {
									event.preventDefault();
									navigate({ destination: 'workspace', section: 'resources', recordId: null });
								}}
							>
								Resources
							</a>{' '}
							for what the figures are and are not computed from.
						</p>
					</>
				)}
			</section>

			{/* ------------------------------------------- conversations archive */}
			<section class="panel" aria-labelledby="archive-heading">
				<h2 id="archive-heading">Earlier conversations</h2>
				<p class="muted small">
					<strong>Read-only.</strong> There is one conversation you speak in — the Main Chat — and
					nothing here can become a second one. These are records of conversations that already
					happened, and this screen has no composer.
				</p>
				{archiveState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the archive…
					</p>
				)}
				{archiveState.status === 'failed' && archiveState.error !== null && (
					<Failure error={archiveState.error} what="the archive" onRetry={() => void archive.load()} />
				)}
				{archiveState.status === 'ready' && (
					<>
						{conversations.length === 0 ? (
							<p>
								<strong>No conversation is on record.</strong>{' '}
								<span class="muted">
									The Main Chat is created the first time you open it, so an empty archive beside
									a working chat means this store has only ever held the one.
								</span>
							</p>
						) : (
							<ul class="records">
								{conversations.map((conversation) => (
									<li key={conversation.conversationId} class="record">
										<div class="record-head">
											<strong>{conversation.title}</strong>
											{conversation.isMainChat ? (
												<span class="tag tag-live">the Main Chat</span>
											) : (
												<span class="tag">archive</span>
											)}
										</div>
										<Technical summary="Conversation identifier">
											<code>{conversation.conversationId}</code>
										</Technical>
										<p class="muted small">
											{conversation.messageCount} turn(s) ·{' '}
											<time dateTime={conversation.updatedAt}>{conversation.updatedAt}</time>
											{conversation.targetKey !== null && (
												<>
													{' '}
													· target <code>{conversation.targetKey}</code>
												</>
											)}
										</p>
										<p>
											<button
												type="button"
												class="button ghost"
												aria-expanded={openId === conversation.conversationId}
												onClick={() =>
													openId === conversation.conversationId
														? (setOpenId(null), setOpenPage(null))
														: void openArchive(conversation.conversationId)
												}
											>
												{openId === conversation.conversationId ? 'Close' : 'Read the archive'}
											</button>
										</p>
										{openId === conversation.conversationId && (
											<ArchivePane page={openPage} error={openError} isMainChat={conversation.isMainChat} />
										)}
									</li>
								))}
							</ul>
						)}
						{archiveError !== null && (
							<Failure
								error={archiveError}
								what="the older conversations"
								onRetry={() => void loadOlderConversations()}
							/>
						)}
						{archiveData?.hasOlder === true && (
							<p>
								<button
									type="button"
									class="button"
									disabled={paging}
									onClick={() => void loadOlderConversations()}
								>
									{paging ? 'Reading…' : 'Read older conversations'}
								</button>
							</p>
						)}
					</>
				)}
			</section>
		</section>
	);
}

/** ONE ARCHIVED CONVERSATION, RENDERED AS A RECORD RATHER THAN A CHAT.
 *
 *  NO COMPOSER, NO REPLY, NO "CONTINUE" — and the panel says why. The turns are
 *  rendered exactly as the Main Chat renders them, text nodes only, because the
 *  honesty rules about markup and truncation do not stop applying to a
 *  conversation just because it is old. */
function ArchivePane({
	page,
	error,
	isMainChat,
}: {
	page: ArchivedPage | null;
	error: GatewayError | null;
	isMainChat: boolean;
}) {
	if (error !== null) {
		return <Failure error={error} what="this conversation" onRetry={() => undefined} />;
	}
	if (page === null) {
		return (
			<p class="notice" role="status">
				Reading this conversation…
			</p>
		);
	}
	if (!page.found) {
		return (
			<p class="notice">
				<strong>This conversation is not on record for you.</strong> The read returned nothing for{' '}
				<code>{page.conversationId}</code>, which is what a conversation belonging to nobody — or to
				somebody else — answers with.
			</p>
		);
	}
	return (
		<div class="archive-pane">
			<p class="notice small">
				<strong>Archive.</strong>{' '}
				{isMainChat
					? 'This is the Main Chat, shown here as a record. Talk to Scout in the Main Chat itself.'
					: 'This conversation is closed. It is shown as it was recorded, and there is nowhere here to add to it.'}
			</p>
			{page.hasOlder && (
				<p class="muted small">
					Showing the newest {page.messages.length} turn(s); {page.messages.length < 1 ? 'none' : 'older ones are'} not
					loaded.
				</p>
			)}
			<ul class="archive-turns">
				{page.messages.map((turn) => (
					<li key={turn.messageId} class={`turn turn-${turn.role.toLowerCase()}`}>
						<header class="turn-head">
							<span class="turn-who">{turn.role === 'OWNER' ? 'You' : 'Scout'}</span>
							<time class="turn-when" dateTime={turn.createdAt} title={turn.createdAt}>
								{formatTurnTime(turn.createdAt)}
							</time>
							{turn.reasoning === 'EXPLORATORY' && <span class="turn-label">exploratory</span>}
							{turn.reasoning === 'EVIDENCE_REPORT' && (
								<span class="turn-label">evidence report</span>
							)}
						</header>
						{/* TEXT NODES ONLY. Preact escapes by default and nothing here
						    opts out — an archived turn is exactly as untrusted as a live
						    one. */}
						<div class="turn-body">{turn.content}</div>
						{turn.completion !== null && turn.completion !== 'COMPLETE' && (
							<p class="turn-completion">
								Scout’s answer did not finish ({turn.completion}). What is shown is what was
								produced.
							</p>
						)}
					</li>
				))}
			</ul>
		</div>
	);
}
